import React, { useMemo, useState } from 'react';
import {
	Button,
	Space,
	Table,
	Typography,
} from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

import type { ProductionQuestion } from '../../../interfaces/ProductionQuestion.interface';
import { useGetOrderLineItemProductionQuestionsQuery } from '../../../redux/api/production-questions';
import { useGetLoggedInUserQuery } from '../../../redux/api/users';
import { canEditProductionQuestion } from '../../../utils/canEditProductionQuestion';
import QuestionDetailsModal from './QuestionDetailsModal';
import QuestionFormModal from './QuestionFormModal';
import QuestionStatusControl from './QuestionStatusControl';
import {
	ID_COLUMN,
	REPORTER_COLUMN,
	RESOLUTION_CATEGORY_COLUMN,
	RESOLUTION_NOTES_COLUMN,
} from './tableColumns';

const { Text } = Typography;

interface Props {
	lineItemId: string;
	disabled?: boolean;
}

const ProductionQuestions: React.FC<Props> = ({
	lineItemId,
	disabled = false,
}) => {
	const { data: loggedInUser } = useGetLoggedInUserQuery();
	const { data, isFetching } = useGetOrderLineItemProductionQuestionsQuery(
		lineItemId,
		{ skip: !lineItemId }
	);
	const [formModalOpen, setFormModalOpen] = useState(false);
	const [detailsOpen, setDetailsOpen] = useState(false);
	const [editing, setEditing] = useState<ProductionQuestion | null>(null);
	const [viewing, setViewing] = useState<ProductionQuestion | null>(null);

	const openDetails = (row: ProductionQuestion) => {
		setViewing(row);
		setDetailsOpen(true);
	};

	const openEdit = (row: ProductionQuestion) => {
		setEditing(row);
		setFormModalOpen(true);
	};

	const columns: ColumnsType<ProductionQuestion> = useMemo(
		() => [
			ID_COLUMN,
			{
				title: 'Category',
				dataIndex: 'category_display',
				key: 'category_display',
			},
			REPORTER_COLUMN,
			RESOLUTION_CATEGORY_COLUMN,
			RESOLUTION_NOTES_COLUMN,
			{
				title: 'Status',
				key: 'status',
				render: (_, row) => (
					<QuestionStatusControl
						question={row}
						lineItemId={lineItemId}
						disabled={disabled}
						onUpdated={(updated) => {
							if (viewing?.id === updated.id) {
								setViewing(updated);
							}
						}}
					/>
				),
			},
			{
				title: 'Actions',
				key: 'actions',
				align: 'right',
				width: 180,
				render: (_, row) => (
					<Space size="small" className="whitespace-nowrap">
						{canEditProductionQuestion(loggedInUser, row) && (
							<Button
								type="link"
								icon={<EditOutlined />}
								onClick={() => openEdit(row)}
								disabled={disabled}
							>
								Edit
							</Button>
						)}
						<Button onClick={() => openDetails(row)}>Details</Button>
					</Space>
				),
			},
		],
		[disabled, lineItemId, viewing?.id, loggedInUser]
	);

	return (
		<>
			<div className="mb-3 flex items-center justify-between gap-3">
				<Text type="secondary">
					Ask clarifying questions about drawings or models with screenshots and
					track customer response.
				</Text>
				<Button
					type="primary"
					icon={<PlusOutlined />}
					onClick={() => {
						setEditing(null);
						setFormModalOpen(true);
					}}
					disabled={disabled}
				>
					New Question
				</Button>
			</div>

			<Table
				rowKey="id"
				columns={columns}
				dataSource={data ?? []}
				loading={isFetching}
				pagination={false}
				size="small"
			/>

			<QuestionFormModal
				lineItemId={lineItemId}
				question={editing}
				open={formModalOpen}
				onClose={() => {
					setFormModalOpen(false);
					setEditing(null);
				}}
				disabled={disabled}
			/>

			<QuestionDetailsModal
				question={viewing}
				open={detailsOpen}
				onClose={() => setDetailsOpen(false)}
				afterClose={() => setViewing(null)}
				lineItemId={lineItemId}
				disabled={disabled}
				onQuestionUpdated={setViewing}
				onEdit={
					viewing && canEditProductionQuestion(loggedInUser, viewing) && !disabled
						? () => {
								setDetailsOpen(false);
								openEdit(viewing);
							}
						: undefined
				}
			/>
		</>
	);
};

export default ProductionQuestions;
